import React, { PureComponent } from "react";
import { Link } from "react-router";
import { Loader } from "react-loaders";
import { find, flatten, isNil, map, some, pick, fromPairs, zip } from "lodash";
import { pure } from "recompose";

import { changeLocation } from '../../api/Location';
import { getRequest, batchRequest } from "../../api/ApiService";
import { logError } from "../../services/utils";
import Auth from "routes/Auth";
import { getTeam, getCompetitionTeams } from "./utils";
import { fontFamily } from "./styles";
import Paper from "./Paper";
import Menu from "./Menu";
import TeamReports from "./TeamReports";
import GradesRanks from "./GradesRanks";
import MarketShare from "./MarketShare";
import Revenues from "./Revenues";
import GrossMargins from "./GrossMargins";
import SharePrice from "./SharePrice";
import EarningsPerShare from "./EarningsPerShare";
import ReturnOnAssets from "./ReturnOnAssets";
import ReturnOnEquity from "./ReturnOnEquity";
import FPA from "./FPA";
import FPAIncomeStatement from "./FPAIncomeStatement";
import LeadTime from "./LeadTime";
import MarketingAnalysis from "./MarketingAnalysis";
import MarketingAnalysis1 from "./MarketingAnalysis1";
import MarketingNoUnitsSold1 from "./MarketingNoUnitsSold1";
import MarketingNoUnitsSold2 from "./MarketingNoUnitsSold2";
import MarketingPositioning from "./MarketingPositioning";
import MarketingSellingPrice from "./MarketingSellingPrice";
import MarketingAwareness from "./MarketingAwareness";
import MarketingFinancialImpact1 from "./MarketingFinancialImpact1";
import MarketingFinancialImpact2 from "./MarketingFinancialImpact2";
import OperationalAnalysis from "./OperationalAnalysis";
import UnitCosts1 from "./UnitCosts1";
import UnitCosts2 from "./UnitCosts2";
import InventoryManagement from "./InventoryManagement";
import OAFinancialImpact from "./OAFinancialImpact";
import FinancingAnalysis from "./FinancingAnalysis";
import CapitalStructure from "./CapitalStructure";
import CostOfDebt from "./CostOfDebt";
import ImpactOfDebt from "./ImpactOfDebt";
import CostOfCapital from "./CostOfCapital";
import Valuation from "./Valuation";
import Shares from "./Shares";
import FinancialStatements from "./FinancialStatements";
import IncomeStatement from "./IncomeStatement";
import BalanceSheet from "./BalanceSheet";
import CashFlows from "./CashFlows";
import Teams from "../../api/Teams";

const CrumbSeparator = pure(() => (
  <span
    style={{
      marginLeft: "1ch",
      marginRight: "1ch",
      fontSize: "12px",
      fontFamily,
      color: "#828282"
    }}
  >
    >
  </span>
));

const BreadCrumbs = pure(({ crumbs }) => (
  <div style={{ marginTop: "1em", marginBottom: "1em" }}>
    {flatten(
      map(crumbs, ({ location, label }, index) => [
        index !== 0 ? (
          <CrumbSeparator key={`${location}:${label}-sep`} />
        ) : null,
        <Link
          to={location}
          style={{ fontSize: "12px", fontFamily, color: "#828282" }}
          key={`${location}:${label}`}
        >
          {label}
        </Link>
      ])
    )}
  </div>
));

const APICalls = gameId => [
  //{ url: 'static/constants', key: 'constants', round: false },
  { url: "static/total_demand", key: "totalDemand" },
  {
    url: "static/comparable_companies",
    key: "comparableCompanies"
  },
  { url: "team/manufacture_costs", key: "manufactureCosts" },
  { url: "team/inventory_balances", key: "inventoryBalances" },
  { url: "team/report/balance_sheet", key: "balanceSheet" },
  { url: "competition/metrics", key: "competitionMetrics" },
  { url: "teams", key: "teams" },
  {
    url: "team/report/income_statement",
    key: "incomeStatement"
  },
  {
    url: "team/financial_analysis",
    key: "financialAnalysis",
    round: "lteFinancial"
  },
  { url: "team/report/cashflow", key: "cashFlows" },
  {
    url: "competition/competitive_analysis",
    key: "competitiveAnalysis"
  },
  {
    url: "competition/competitive_analysis",
    key: "competitiveAnalysisAll",
    round: "lte"
  },
  { url: "team/report/metrics", key: "metrics" },
  { url: "v2/finance/metricsreports", key: "metricsReports", round: 'id' },
  {
    url: `games/${gameId}/top_performers`,
    key: "topPerformers",
    round: 'attachCompetition',
  },
  { url: "team/unit_sales", key: "unitSales" },
  { url: "team/products", key: "products" },
  { url: "team/price_forecasts", key: "forecasts" },
  { url: "team/equities", key: "equities" },
  { url: "team/stock_repurchased", key: "stockRepurchased" },
  { url: "team/projections", key: "projections" },
  { url: "team/page/ceo", key: "ceo" }
];

const Content = pure(({ ifLoaded, active, hasAccess }) =>
  hasAccess === true ? (
    (
      {
        "": () =>
          ifLoaded(
            ["grades", "teamRank", "round", "isFinished", "location"],
            TeamReports
          ),
        "grades-ranks": () => ifLoaded(["round"], GradesRanks),
        "market-share": () =>
          ifLoaded(["teams", "competitionMetrics"], MarketShare),
        revenues: () => ifLoaded(["teams", "competitionMetrics"], Revenues),
        "gross-margins": () =>
          ifLoaded(["teams", "competitionMetrics"], GrossMargins),
        "share-price": () =>
          ifLoaded(["constants", "teams", "competitionMetrics"], SharePrice),
        "earnings-per-share": () =>
          ifLoaded(["teams", "competitionMetrics"], EarningsPerShare),
        "return-on-assets": () =>
          ifLoaded(["teams", "competitionMetrics"], ReturnOnAssets),
        "return-on-equity": () =>
          ifLoaded(["teams", "competitionMetrics"], ReturnOnEquity),
        "fp-a": () => <FPA />,
        "financial-planning-and-analysis": () =>
          ifLoaded(
            [
              "teams",
              "teamId",
              "incomeStatement",
              "financialAnalysis",
              "round",
              "rounds"
            ],
            FPAIncomeStatement
          ),
        "marketing-analysis": () => <MarketingAnalysis />,
        "market-share-capture": () =>
          ifLoaded(
            [
              "competitiveAnalysis",
              "competitiveAnalysisTeam",
              "teams",
              "totalDemand",
              "competitionMetrics"
            ],
            MarketingAnalysis1
          ),
        "low-sales-1": () =>
          ifLoaded(["financials", "round"], MarketingNoUnitsSold1),
        "low-sales-2": () => ifLoaded(["messages"], MarketingNoUnitsSold2),
        "customer-survey": () =>
          ifLoaded(
            [
              "competitiveAnalysis",
              "teamId",
              "topPerformers",
              "teams",
              "rounds"
            ],
            MarketingPositioning
          ),
        "marketing-selling-price": () =>
          ifLoaded(
            [
              "competitiveAnalysis",
              "teamId",
              "unitSales",
              "teams",
              "products",
              "forecasts",
              "competitionMetrics",
              "rounds",
              "round"
            ],
            MarketingSellingPrice
          ),
        "marketing-awareness": () =>
          ifLoaded(
            [
              "incomeStatement",
              "competitiveAnalysis",
              "teamId",
              "teams",
              "competitionMetrics"
            ],
            MarketingAwareness
          ),
        "marketing-financial-impact-1": () =>
          ifLoaded(["incomeStatement"], MarketingFinancialImpact1),
        "marketing-financial-impact-2": () =>
          ifLoaded(["incomeStatement"], MarketingFinancialImpact2),
        "operational-analysis": () => <OperationalAnalysis />,
        "operational-analysis-unit-costs": () =>
          ifLoaded(
            ["competitiveAnalysis", "teams", "teamId", "competitionMetrics"],
            UnitCosts1
          ),
        "operational-analysis-unit-costs-breakdown": () =>
          ifLoaded(
            ["manufactureCosts", "round", "rounds", "teamId"],
            UnitCosts2
          ),
        "operational-analysis-inventory-management": () =>
          ifLoaded(
            [
              "constants",
              "inventoryBalances",
              "competitiveAnalysis",
              "products",
              "teamId",
              "teams",
              "competitionMetrics",
              "rounds",
              "round"
            ],
            InventoryManagement
          ),
        "operational-analysis-lead-time": () =>
          ifLoaded(
            [
              "teamId",
              "teams",
              "competitionMetrics",
              "metricsReports"
            ],
            LeadTime
          ),
        "operational-analysis-financial-impact": () =>
          ifLoaded(["incomeStatement"], OAFinancialImpact),
        "financing-analysis": () =>
          ifLoaded(["ceo", "competitiveAnalysis", "teamId"], FinancingAnalysis),
        "financing-analysis-capital-structure": () =>
          ifLoaded(
            ["teams", "competitionMetrics", "metrics"],
            CapitalStructure
          ),
        "financing-analysis-cost-of-debt": () =>
          ifLoaded(["metrics"], CostOfDebt),
        "financing-analysis-impact-of-debt": () =>
          ifLoaded(["incomeStatement"], ImpactOfDebt),
        "financing-analysis-cost-of-capital": () =>
          ifLoaded(["metrics", "teams", "competitionMetrics"], CostOfCapital),
        "financing-analysis-valuation": () =>
          ifLoaded(
            [
              "round",
              "teams",
              "competitiveAnalysis",
              "competitiveAnalysisAll",
              "competitionMetrics",
              "teamId",
              "comparableCompanies",
              "constants"
            ],
            Valuation
          ),
        "financing-analysis-shares": () =>
          ifLoaded(
            [
              "constants",
              "metrics",
              "teams",
              "competitionMetrics",
              "equities",
              "stockRepurchased",
              "teamId",
              "round",
              "roundId"
            ],
            Shares
          ),
        "financial-statements": () => <FinancialStatements />,
        "income-statement": () =>
          ifLoaded([
            "incomeStatement",
            "round",
            "rounds"
          ], IncomeStatement),
        "balance-sheet": () => ifLoaded(["balanceSheet", "round", "constants"], BalanceSheet),
        "cash-flows": () => ifLoaded(["cashFlows", "round"], CashFlows)
      }[active] || (() => <Paper>Page Not Found</Paper>)
    )()
  ) : (
    <Paper>Page Not Found</Paper>
  )
);

const getQueryVariable = variable => {
  const temporaryUser = Auth.getTemporaryUser();
  if (temporaryUser && temporaryUser[variable]) {
    return temporaryUser[variable];
  }

  console.log("Temporary user doesn't have %s field", variable);
};

export default class Analysis extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      ...this.initialState,
      currentRound: props.currentRound,
      roundInUrl: props.roundInUrl
    };

    if (Auth.getPosition() === "Student") {
      const team = Teams.currentTeam();
      this.teamId = team ? team.id : 0;
    } else {
      this.teamId = parseInt(getQueryVariable("team_id"), 10);
    }
  }

  initialState = {
    //  team: null,
    grades: [],
    //  gradesLte: [],
    balanceSheet: null,
    competitiveAnalysis: null,
    competitiveAnalysisAll: null,
    competitionMetrics: null,
    metricsReports: null,
    incomeStatement: null,
    cashFlows: null,
    metrics: null,
    messages: null,
    projections: null,
    products: null,
    forecasts: null,
    promos: null,
    sales: null,
    financials: null,
    manufactureCosts: null,
    teamGrade: "",
    topPerformers: null,
    unitSales: null,
    ceo: null,
    equities: null,
    stockRepurchased: null,
    comparableCompanies: null,
    inventoryBalances: null,
    teams: null,
    tmpTeamGrade: 0,
    tmpRankName: "",
    tmpRounds: []
  };

  componentWillReceiveProps(nextProps) {
    if (
      nextProps.currentRound !== this.state.currentRound ||
      nextProps.roundInUrl !== this.state.roundInUrl ||
      nextProps.rounds !== this.props.rounds
    ) {
      this.setState(
        {
          currentRound: nextProps.currentRound,
          roundInUrl: nextProps.roundInUrl,
        },
        () => {
          this.fetchData(this.getRound());
        }
      );
    }
  }

  fetchDataForInstructor = apiCalls => {
    getRequest(`${process.env.REACT_APP_API_URL}teams`)
      .then(teams => {
        const currentTeam = teams.body.find(t => Number(t.id) === this.teamId);
        const competition = currentTeam ? Number(currentTeam.competition) : 0;

        batchRequest(
          map(
            apiCalls,
            call =>
              `${process.env.REACT_APP_API_URL}${call.url}` +
              {
                false: () =>
                  `?round_number=${this.getRound()}&team_id=${this.teamId}`,
                undefined: () =>
                  `/?round_number=${this.getRound()}&team_id=${this.teamId}`,
                lte: () => `/?round_lte=${this.getRound()}&team_id=${this.teamId}`,
                lteFinancial: () =>
                  `/?round_lte=${this.getRound() + 1}&team_id=${this.teamId}`,
                id: () => `/?round=${this.getRoundId()}`,
                attachCompetition: () =>
                  `?round_number=${this.getRound()}&team_id=${this.teamId}&competition=${competition}`,
              }[call.round]()
          )
        ).then(res => {
          const newState = fromPairs(
            map(zip(res, apiCalls), ([{ body }, { key }]) => [
              key,
              body
            ])
          );
          const roundTmp = this.state.tmpRounds.find(
            i => i.number === this.getRound()
          );
          const roundId = roundTmp ? roundTmp.id : 0;
          newState.competitionMetrics = null;
          newState.incomeStatement = newState.incomeStatement.find(
            i => i.round === roundId
          );
          newState.balanceSheet = newState.balanceSheet.find(
            i => i.round === roundId
          );
          newState.cashFlows = newState.cashFlows.find(i => i.round === roundId);
          newState.metrics = newState.metrics.find(i => i.round === roundId);
          this.setState(newState, () => {
            this.fetchCompetitionMetricsForInstructor();
          });
        });
        this.fetchConstants(this.getRound());

        if (Auth.getPosition() !== "Student") {
          this.fetchTeamGradesForInstructor(this.getRound());
          this.fetchRoundsForInstructor();
        }
      })
      .catch(errTeams => { });
  };

  getCompetitionMetrics = data => {
    let result = {};

    Object.keys(data).forEach(key => {
      const ignores = [
        "id",
        "round",
        "team",
        "created_at",
        "updated_at",
        "is_actual",
        "reason",
        "round_number"
      ];
      const convert = {
        salesrep: "SalesRep",
        roa: "ROA",
        roe: "ROE",
        ros: "ROS",
        roic: "ROIC",
        capm: "CAPM",
        ebitda: "EBITDA",
        rnoa: "RNOA",
        wacc: "WACC",
        market_demand_auto: "Market Demand - Auto",
        market_demand_mobile: "Market Demand - Mobile",
        avg_revenues_per_unit_auto: "Avg Revenues Per Unit - Auto",
        avg_revenues_per_unit_mobile: "Avg Revenues Per Unit - Mobile",
        contribution_margin_percent: "Contribution Margin %",
        depreciation_sales_related: "Depreciation-Sales Related",
        equity_cost_of_capital: "Equity Cost of Capital",
        oh_cost_per_unit: "OH Cost Per Unit",
        price_to_ebitda: "Price to EBITDA",
        profit_margin_percent: "Profit Margin %",
        required_rate_of_return: "Required Rate of Return",
        sgna_costs_percent_sales: "SG&A Costs % Sales"
      };
      const ucFirst = str => str[0].toUpperCase() + str.slice(1);
      const newKey = ignores.includes(key)
        ? key
        : key in convert
          ? convert[key]
          : key
            .split("_")
            .map(i => (i === "to" ? i : ucFirst(i)))
            .join(" ");

      if (!["game", "competition"].includes(key)) {
        result[newKey] = data[key];
      }
    });

    return result;
  };

  fetchCompetitionMetricsForInstructor = () => {
    const { teams } = this.state;
    const team = teams.find(i => i.id === this.teamId);

    if (team) {
      const { competition } = team;

      getRequest(
        `${process.env.REACT_APP_API_URL}v2/finance/metricsreports/?competition=${competition}&round=${this.getRoundId()}`
      )
        .then(res => {
          this.setState({
            competitionMetrics: res.body.results.map(i => this.getCompetitionMetrics(i)),
          });
        })
        .catch(err => { });
    }
  };

  fetchRoundsForInstructor = () => {
    getRequest(`${process.env.REACT_APP_API_URL}game/rounds/`)
      .then(res => {
        const game_id = parseInt(getQueryVariable("game_id"), 10);

        this.setState({
          tmpRounds: res.body.filter(i => i.game === game_id)
        });
      })
      .catch(err => { });
  };

  fetchTeamGradesForInstructor = round => {
    getRequest(
      `${process.env.REACT_APP_API_URL}team/grading/?round_number=${round}&team_id=${this.teamId}`
    )
      .then(res => {
        let tmpTeamGrade = 0;
        let tmpRankName = "Unknown Rank";

        const obj = res.body.find(i => i.round_number === round);
        if (obj) {
          tmpTeamGrade = obj.weighted_value * 100;
          if (obj.ranking !== "") {
            tmpRankName = obj.ranking;
          }
        }

        this.setState({
          grades: res.body,
          tmpTeamGrade,
          tmpRankName
        });
      })
      .catch(err => { });
  };

  fetchData(round) {
    const apiCalls = APICalls(
      Auth.getPosition() === "Student"
        ? Auth.getGameId()
        : parseInt(getQueryVariable("game_id"), 10)
    );
    const { rounds } = this.props;

    if (Auth.getPosition() !== "Student") {
      this.fetchDataForInstructor(apiCalls);
      return;
    }

    if (!rounds || rounds.length === 0) {
      return;
    }
    const maxRound = rounds[rounds.length - 1].number;
    const roundFinancial = round + 1 > maxRound ? maxRound : round + 1;
    const roundObj = rounds.find(i => Number(i.number) === round);
    const roundId = roundObj ? Number(roundObj.id) : 0;
    batchRequest(
      map(
        apiCalls,
        call =>
          `${process.env.REACT_APP_API_URL}${call.url}` +
          {
            false: () => "",
            undefined: () => `/?round_number=${round}`,
            lte: () => `/?round_lte=${round}`,
            lteFinancial: () => `/?round_lte=${roundFinancial}`,
            id: () => `/?round=${roundId}`,
            attachCompetition: () => `/?round_number=${round}`,
          }[call.round]()
      )
    ).then(res => {
      const newState = fromPairs(
        map(zip(res, apiCalls), ([{ body }, { key }]) => [
          key,
          body
        ])
      );
      const roundId = rounds.find(i => i.number === this.getRound()).id;
      try {
        newState.incomeStatement = newState.incomeStatement.find(
          i => i.round === roundId
        );
      } catch (err) {
        newState.incomeStatement = null;
      }
      try {
        newState.balanceSheet = newState.balanceSheet.find(
          i => i.round === roundId
        );
      } catch (err) {
        newState.balanceSheet = null;
      }
      try {
        newState.cashFlows = newState.cashFlows.find(i => i.round === roundId);
      } catch (err) {
        newState.cashFlows = null;
      }
      try {
        newState.metrics = newState.metrics.find(i => i.round === roundId);
      } catch (err) {
        newState.metrics = null;
      }
      this.setState(newState);
    });
    this.fetchConstants(round);
    this.fetchTeamGrades(round);
    //this.fetchCEO(round)
    /*
    this.fetchBalance()
    this.fetchCompetitionMetrics(round)
    this.fetchTeams(round)
    this.fetchMetrics(round)
    this.fetchIncomeStatement(round)
    this.fetchBalanceSheet(round)
    this.fetchCashFlows(round)
    this.fetchTotalDemand(round)
    this.fetchTeamGradesLte()
    this.fetchCompetitiveAnalysis(round)
    this.fetchCompetitiveAnalysisAll(round)
    this.fetchFinancialAnalysis(round)
    this.fetchProjections()
    this.fetchProducts(round)
    this.fetchForecasts(round)
    this.fetchPromos(round)
    this.fetchSales(round)
    this.fetchFinancials(round)
    this.fetchMessages(round)
    this.fetchManufactureCosts(round)
    this.fetchTopPerformers(round)
    this.fetchUnitSales(round)
    this.fetchEquities(round)
    this.fetchStockRepurchased(round)
    this.fetchComparableCompanies(round)
    this.fetchInventoryBalances(round)
    */
  }

  getRound() {
    const { currentRound, roundInUrl } = this.state;

    return roundInUrl || currentRound;
  }

  getRoundId() {
    const { rounds } = this.props;
    const roundNumber = this.getRound();
    const round = rounds.find(i => Number(i.number) === roundNumber);

    return round ? Number(round.id) : 0;
  }

  getCountOfRounds() {
    const { tmpRounds } = this.state;
    const { rounds } = this.props;

    return Auth.getPosition() === "Student" ? rounds.length : tmpRounds.length;
  }

  componentDidMount() {
    if (Auth.getPosition() === 'Instructor') {
      const { rounds } = this.props;
      const roundNumber = Number(this.getRound());

      const activeRound = rounds.find(item => item.number === roundNumber);
      const isActiveRoundFinished = activeRound ? activeRound.is_finished : false;

      if (!isActiveRoundFinished) {
        changeLocation(
          `/decisions/ceo?round=${roundNumber}`
        );
      }
    }
    this.fetchData(this.getRound());
  }

  fetchConstants() {
    getRequest(`${process.env.REACT_APP_API_URL}static/constants/`).then(
      ({ body }) => {
        this.setState({
          constants: body[0]
        });
      }
    );
  }

  fetchManufactureCosts(round) {
    getRequest(
      `${process.env.REACT_APP_API_URL}team/manufacture_costs/?round_number=${round}`
    ).then(res => {
      let avgMaterialCostsPerUnit = 0;
      let avgLaborCostsPerUnit = 0;
      let avgOverheadCostsPerUnit = 0;

      res.body.forEach(product => {
        avgMaterialCostsPerUnit += product.material_costs;
        avgLaborCostsPerUnit += product.labor_costs;
        avgOverheadCostsPerUnit += product.overhead_costs;
      });

      this.setState({
        avgMaterialCostsPerUnit,
        avgLaborCostsPerUnit,
        avgOverheadCostsPerUnit
      });
    });
  }

  fetchTotalDemand(round) {
    getRequest(
      `${process.env.REACT_APP_API_URL}static/total_demand/?round_number=${round}`
    ).then(({ body }) => {
      this.setState({
        totalDemand: body
      });
    });
  }

  fetchInventoryBalances(round) {
    getRequest(
      `${process.env.REACT_APP_API_URL}team/inventory_balances/?round_number=${round}`
    ).then(({ body }) => {
      this.setState({
        inventoryBalances: body
      });
    });
  }

  fetchComparableCompanies(round) {
    getRequest(
      `${process.env.REACT_APP_API_URL}static/comparable_companies/?round_number=${round}`
    ).then(({ body }) => {
      this.setState({
        comparableCompanies: body
      });
    });
  }

  fetchTopPerformers(round) {
    getRequest(
      `${process.env.REACT_APP_API_URL
      }games/${Auth.getGameId()}/top_performers/?round_number=${round}`
    ).then(({ body }) => {
      this.setState({
        topPerformers: body
      });
    });
  }

  fetchEquities(round) {
    getRequest(
      `${process.env.REACT_APP_API_URL}team/equities/?round_number=${round}`
    ).then(({ body }) => {
      this.setState({
        equities: body
      });
    });
  }

  fetchStockRepurchased(round) {
    getRequest(
      `${process.env.REACT_APP_API_URL}team/stock_repurchased/?round_number=${round}`
    ).then(({ body }) => {
      this.setState({
        stockRepurchased: body
      });
    });
  }

  fetchMetrics(round) {
    getRequest(
      `${process.env.REACT_APP_API_URL}team/report/metrics/?round_number=${round}`
    ).then(({ body }) => {
      this.setState({
        metrics: body[0]
      });
    });
  }

  fetchFinancialAnalysis(round) {
    const { rounds } = this.props;
    const maxRound = rounds[rounds.length - 1].number;
    const activeRound = round + 1 > maxRound ? maxRound : round + 1;

    getRequest(
      `${process.env.REACT_APP_API_URL}team/financial_analysis/?round_lte=${activeRound}`
    ).then(({ body }) => {
      this.setState({
        financialAnalysis: body
      });
    });
  }

  fetchTeams() {
    getRequest(`${process.env.REACT_APP_API_URL}teams/`).then(({ body }) => {
      this.setState({
        teams: body
      });
    });
  }

  fetchCompetitionMetrics(round) {
    getRequest(
      `${process.env.REACT_APP_API_URL}competition/metrics/?round_number=${round}`
    ).then(({ body }) => {
      this.setState({
        competitionMetrics: body
      });
    });
  }

  fetchUnitSales(round) {
    getRequest(
      `${process.env.REACT_APP_API_URL}team/unit_sales/?round_number=${round}`
    ).then(({ body }) => {
      this.setState({
        unitSales: body
      });
    });
  }

  fetchCEO(round) {
    getRequest(
      `${process.env.REACT_APP_API_URL}team/page/ceo/?round_number=${round}`
    ).then(({ body }) => {
      this.setState({
        ceo: body
      });
    });
  }

  fetchMessages(round) {
    getRequest(
      `${process.env.REACT_APP_API_URL}teams/messages/?round_number=${round}`
    ).then(({ body }) => {
      this.setState({
        messages: body
      });

      if (body["type_name"]) {
        if (
          body.minimum_requirement_fail ||
          body.price_range_fail ||
          body.distribution_fail
        ) {
          this.setState({
            conditionalPage: true
          });
        }
      }
    });
  }

  getIncomeStatement(round) {
    return getRequest(
      `${process.env.REACT_APP_API_URL}team/report/income_statement/?round_number=${round}`
    );
  }

  fetchIncomeStatement = round => {
    this.getIncomeStatement(round)
      .then(res => {
        this.setState({
          incomeStatement: res.body[0]
        });
      })
      .catch(err => {
        console.log("income err", err);
      });
  };

  getBalanceSheet(round) {
    return getRequest(
      `${process.env.REACT_APP_API_URL}team/report/balance_sheet/?round_number=${round}`
    );
  }

  fetchBalanceSheet = round => {
    this.getBalanceSheet(round)
      .then(res => {
        this.setState({
          balanceSheet: res.body[0]
        });
      })
      .catch(err => {
        console.log("balanceSheet err", err);
      });
  };

  getCashFlows(round) {
    return getRequest(
      `${process.env.REACT_APP_API_URL}team/report/cashflow/?round_number=${round}`
    );
  }

  fetchCashFlows = round => {
    this.getCashFlows(round)
      .then(({ body }) => {
        this.setState({
          cashFlows: body[0]
        });
      })
      .catch(err => {
        console.log("cashFlow err", err);
      });
  };

  fetchBalance = round => {
    if (round) {
      const url = `${process.env.REACT_APP_API_URL}team/page/financials/?round_number=${round}`;

      if (this.balanceRequest) {
        this.balanceRequest.abort();
        this.balanceRequest = null;
      }
      this.balanceRequest = getRequest(url);

      this.balanceRequest
        .then(({ body: { team } }) => {
          this.setState({
            team
          });
        })
        .catch(err => {
          logError(err, "Analysis", "fetchBalance");
        });
    }
  };

  fetchTeamGrades = round => {
    if (round) {
      getRequest(`${process.env.REACT_APP_API_URL}team/grading/?round_number=${round}`)
        .then(({ body }) => {
          this.setState({
            grades: body
          });
        })
        .catch(err => {
          logError(err, "Analysis", "fetchTeamGrades");
        });
    }
  };

  fetchTeamGradesLte = round => {
    if (round) {
      const url = `${process.env.REACT_APP_API_URL}team/grading/?round_lte=${round}`;

      if (this.teamGradeLteRequest) {
        this.teamGradeLteRequest.abort();
        this.teamGradeLteRequest = null;
      }
      this.teamGradeLteRequest = getRequest(url);

      this.teamGradeLteRequest
        .then(({ body }) => {
          this.setState({
            gradesLte: body
          });
        })
        .catch(err => {
          logError(err, "Analysis", "fetchTeamGradesLte");
        });
    }
  };

  fetchCompetitiveAnalysis = round => {
    const url = `${process.env.REACT_APP_API_URL}competition/competitive_analysis/?round_number=${round}`;

    if (this.teamCompetitiveAnalysisRequest) {
      this.teamCompetitiveAnalysisRequest.abort();
      this.teamCompetitiveAnalysisRequest = null;
    }
    this.teamCompetitiveAnalysisRequest = getRequest(url);

    this.teamCompetitiveAnalysisRequest
      .then(({ body }) => {
        this.setState({
          competitiveAnalysis: body
        });
      })
      .catch(err => {
        logError(err, "Analysis", "fetchCompetitiveAnalysis");
      });
  };

  fetchCompetitiveAnalysisAll = round => {
    const url = `${process.env.REACT_APP_API_URL}competition/competitive_analysis/?round_lte=${round}`;

    if (this.teamCompetitiveAnalysisAllRequest) {
      this.teamCompetitiveAnalysisAllRequest.abort();
      this.teamCompetitiveAnalysisAllRequest = null;
    }
    this.teamCompetitiveAnalysisAllRequest = getRequest(url);

    this.teamCompetitiveAnalysisAllRequest
      .then(({ body }) => {
        this.setState({
          competitiveAnalysisAll: body
        });
      })
      .catch(err => {
        logError(err, "Analysis", "fetchCompetitiveAnalysisAll");
      });
  };

  fetchProjections = () => {
    const url = `${process.env.REACT_APP_API_URL}team/projections`;

    if (this.projectionsRequest) {
      this.projectionsRequest.abort();
      this.projectionsRequest = null;
    }
    this.projectionsRequest = getRequest(url);

    this.projectionsRequest
      .then(({ body }) => {
        this.setState({
          projections: body
        });
      })
      .catch(err => {
        logError(err, "Analysis", "fetchProjections");
      });
  };

  fetchProducts = round => {
    const url = `${process.env.REACT_APP_API_URL}team/products?round_number=${round}`;

    if (this.productsRequest) {
      this.productsRequest.abort();
      this.productsRequest = null;
    }
    this.productsRequest = getRequest(url);

    this.productsRequest
      .then(({ body }) => {
        this.setState({
          products: body
        });
      })
      .catch(err => {
        logError(err, "Analysis", "fetchProducts");
      });
  };

  fetchForecasts = round => {
    const url = `${process.env.REACT_APP_API_URL}team/price_forecasts?round_number=${round}`;

    if (this.forecastsRequest) {
      this.forecastsRequest.abort();
      this.forecastsRequest = null;
    }
    this.forecastsRequest = getRequest(url);

    this.forecastsRequest
      .then(({ body }) => {
        this.setState({
          forecasts: body
        });
      })
      .catch(err => {
        logError(err, "Analysis", "fetchForecasts");
      });
  };

  fetchPromos = () => {
    const url = `${process.env.REACT_APP_API_URL}team/promos`;

    if (this.promosRequest) {
      this.promosRequest.abort();
      this.promosRequest = null;
    }
    this.promosRequest = getRequest(url);

    this.promosRequest
      .then(({ body }) => {
        this.setState({
          promos: body
        });
      })
      .catch(err => {
        logError(err, "Analysis", "fetchPromos");
      });
  };

  fetchSales = () => {
    const url = `${process.env.REACT_APP_API_URL}team/sales`;

    if (this.salesRequest) {
      this.salesRequest.abort();
      this.salesRequest = null;
    }
    this.salesRequest = getRequest(url);

    this.salesRequest
      .then(({ body }) => {
        this.setState({
          sales: body
        });
      })
      .catch(err => {
        logError(err, "Analysis", "fetchSales");
      });
  };

  fetchFinancials = round => {
    const url = `${process.env.REACT_APP_API_URL}team/page/financials/?round_number=${round}`;

    if (this.financialsRequest) {
      this.financialsRequest.abort();
      this.financialsRequest = null;
    }
    this.financialsRequest = getRequest(url);
    this.setState({ financials: null });
    this.financialsRequest
      .then(({ body: { team } }) => {
        this.setState({
          financials: team
        });
      })
      .catch(err => {
        logError(err, "Analysis", "fetchFinancials");
      });
  };

  render() {
    const { teamId } = this;
    const { location, team_grade, rank_name, params, rounds } = this.props;
    const {
      totalDemand,
      competitiveAnalysis,
      teams,
      tmpTeamGrade,
      tmpRankName,
      tmpRounds
    } = this.state;
    if (isNil(rounds) && Auth.getPosition() === "Student") {
      return <Loader type="ball-scale-ripple" active />;
    }
    const round = this.getRound();
    const selectedRound = find(rounds, rnd => rnd.number === round);
    if (isNil(selectedRound) && Auth.getPosition() === "Student") {
      return <Loader type="ball-scale-ripple" active />;
    }
    if (isNil(competitiveAnalysis) && Auth.getPosition() !== "Student") {
      return <Loader type="ball-scale-ripple" active />;
    }
    const isFinished =
      Auth.getPosition() === "Student" ? selectedRound.is_finished : true;
    const report1 = isNil(params.report1) ? "" : params.report1;
    const report2 = isNil(params.report2) ? "" : params.report2;
    const hasAccess =
      report1 === "financial-statements" ||
        report1 === "" ||
        (report1 !== "financial-statements" && isFinished === true)
        ? true
        : false;
    const active = report2 !== "" ? report2 : report1;
    const competitionTeams = teams && getCompetitionTeams(teams, teamId);
    const currentRounds = rounds ? rounds : tmpRounds;
    const roundIdObj = currentRounds.find(r => r.number === round);
    const roundId = roundIdObj ? roundIdObj.id : null;
    const extState = {
      ...this.state,
      teams: competitionTeams,
      round,
      roundId,
      countOfRounds: this.getCountOfRounds(),
      teamId,
      teamRank: rank_name ? rank_name : tmpRankName,
      teamGrade: team_grade ? team_grade : tmpTeamGrade,
      totalDemand: find(totalDemand, item => item.round_number === round),
      competitiveAnalysisTeam: getTeam(teamId, competitiveAnalysis),
      rounds: currentRounds,
      isFinished: isFinished,
      location: location
    };
    const ifLoaded = (fields, Component) =>
      some(
        fields.filter(i => i !== "roundInUrl"),
        field => isNil(extState[field])
      ) ? (
        <Loader type="ball-scale-ripple" active />
      ) : (
        <Component {...pick(extState, fields)} />
      );

    return (
      <div className="dashboard">
        {Auth.getPosition() === "Student" && (
          <BreadCrumbs
            crumbs={[
              { label: "Home", location: "/start" },
              { label: "Analysis", location: "/analysis" },
              { label: "Team Reports", location: "/analysis/team-reports" }
            ]}
          />
        )}
        <div style={{ display: "flex", flexDirection: "row" }}>
          {[
            <div key={0} className="team-reports-menu">
              <Menu
                location={location.pathname}
                search={location.search}
                round={this.getRound()}
                messages={this.state.messages}
                isFinished={isFinished}
              />
            </div>,
            <Content
              key={1}
              ifLoaded={ifLoaded}
              active={active}
              hasAccess={hasAccess}
            />
          ]}
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/analysis/Analysis.js